/**
 * notificationService — the client half of the notification center
 * (card VVo3vlre9wI4gUa1KTzI, 2026-09-22). Server half: functions/notifications.js.
 *
 * Every staff push is also a doc in `notifications` { uid, type, title, body,
 * link, tag, createdAt, readAt, expiresAt }. Clients read their OWN and flip
 * `readAt`; nothing else (firestore.rules). Every query here therefore
 * constrains `uid == me` — rules are not filters, and a list without it is
 * denied wholesale rather than trimmed.
 *
 * READS ARE THE CONSTRAINT (reference_firestore_cost_profile). The header bell
 * listens to the UNREAD set only (capped), so a staff member with a clean inbox
 * costs zero document reads per page view after the first; the full list is
 * read only on the Notifications page.
 */
import { db } from '../firebase';
import {
	collection,
	doc,
	query,
	where,
	orderBy,
	limit,
	onSnapshot,
	updateDoc,
	writeBatch,
	serverTimestamp,
} from 'firebase/firestore';

const COLLECTION = 'notifications';
export const UNREAD_CAP = 100;
export const PAGE_CAP = 300;

/** Live unread count for the bell. Returns the unsubscribe. */
export function subscribeUnreadCount(uid, onCount, onError) {
	if (!uid) {
		onCount(0);
		return () => {};
	}
	const q = query(
		collection(db, COLLECTION),
		where('uid', '==', uid),
		where('readAt', '==', null),
		limit(UNREAD_CAP)
	);
	return onSnapshot(q, (snap) => onCount(snap.size), onError);
}

/** Live list, newest first, for the Notifications page. Returns the unsubscribe. */
export function subscribeNotifications(uid, onRows, onError, max = PAGE_CAP) {
	if (!uid) {
		onRows([]);
		return () => {};
	}
	const q = query(
		collection(db, COLLECTION),
		where('uid', '==', uid),
		orderBy('createdAt', 'desc'),
		limit(max)
	);
	return onSnapshot(q, (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), onError);
}

/** Mark one read (true) or unread (false). The rule allows only this field. */
export function setNotificationRead(id, read) {
	return updateDoc(doc(db, COLLECTION, id), { readAt: read ? serverTimestamp() : null });
}

/** Mark every given id read in one batch (≤ 500 per batch; the page caps at 300). */
export async function markAllRead(ids) {
	if (!ids || !ids.length) return;
	const batch = writeBatch(db);
	ids.forEach((id) => batch.update(doc(db, COLLECTION, id), { readAt: serverTimestamp() }));
	await batch.commit();
}

/* ── Pure helpers (tested directly) ───────────────────────────────────────── */

const toDate = (v) => {
	if (!v) return null;
	if (v instanceof Date) return v;
	if (typeof v.toDate === 'function') return v.toDate();
	if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
	return null;
};
export { toDate };

// One place for what each type looks like. FontAwesome only (no emoji).
export const TYPE_META = Object.freeze({
	lesson_cancelled: { icon: 'fa-calendar-xmark', label: 'Cancellation' },
	makeup_booked: { icon: 'fa-calendar-check', label: 'Makeup' },
	registration_request: { icon: 'fa-clipboard-list', label: 'Registration' },
	slot_change_request: { icon: 'fa-arrows-rotate', label: 'Time change' },
	task_assigned: { icon: 'fa-list-check', label: 'Task' },
	task_due: { icon: 'fa-alarm-clock', label: 'Due' },
	coverage: { icon: 'fa-people-arrows', label: 'Coverage' },
	availability: { icon: 'fa-user-clock', label: 'Availability' },
	lesson_substitution: { icon: 'fa-user-check', label: 'Substitution' },
	private_note_mention: { icon: 'fa-lock', label: 'Private file' },
	mail_urgent: { icon: 'fa-envelope-open-text', label: 'Mail' },
	system_error: { icon: 'fa-triangle-exclamation', label: 'System' },
	other: { icon: 'fa-bell', label: 'Notice' },
});
export const typeMeta = (type) => TYPE_META[type] || TYPE_META.other;

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** "Today" / "Yesterday" / "Monday, Sep 21", by the viewer's LOCAL calendar day. */
export function dayLabel(date, now = new Date()) {
	if (!date) return 'Earlier';
	const y = new Date(now);
	y.setDate(y.getDate() - 1);
	if (dayKey(date) === dayKey(now)) return 'Today';
	if (dayKey(date) === dayKey(y)) return 'Yesterday';
	const sameYear = date.getFullYear() === now.getFullYear();
	return `${DOW[date.getDay()]}, ${MON[date.getMonth()]} ${date.getDate()}${sameYear ? '' : `, ${date.getFullYear()}`}`;
}

/** Group rows (already newest-first) into [{ label, rows }] by local day, order kept. */
export function groupByDay(rows, now = new Date()) {
	const groups = [];
	let current = null;
	rows.forEach((row) => {
		const label = dayLabel(toDate(row.createdAt), now);
		if (!current || current.label !== label) {
			current = { label, rows: [] };
			groups.push(current);
		}
		current.rows.push(row);
	});
	return groups;
}

/** "4:52 PM" in the viewer's local time. */
export function timeLabel(date) {
	if (!date) return '';
	const h = date.getHours();
	const m = String(date.getMinutes()).padStart(2, '0');
	return `${((h + 11) % 12) + 1}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** Bell badge text: nothing at zero, "99+" past the cap. */
export function badgeText(count) {
	if (!count || count < 1) return '';
	return count > 99 ? '99+' : String(count);
}
